// @flow
import styled, { css } from 'styled-components';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { grid } from '@finect/tabular-utils/grid';
import { CardRow, NewSectionInner } from '../../components';

// Ancho de la escena, calculado con el util grid() (no a mano). grid(10) ≈ 1142px.
export const SceneInner = styled(NewSectionInner)`
  max-width: ${grid(10)}px;
`;

// La píldora se mantiene a su ancho recomendado (grid(8) ≈ 904 ≈ 900, como en los modales),
// centrada dentro de la escena más ancha.
export const PillArea = styled.div`
  max-width: ${grid(8)}px;
  margin: 0 auto;
`;

// Aviso legal justo encima del listado de asesores.
export const ListDisclaimer = styled.div`
  margin-bottom: 18px;

  p {
    margin-top: 0;
    color: ${({ theme }) => theme.colors.fontL};
  }
`;

/* ---------------------------------------------------------------------------
   Card de asesor — dos regiones que fluyen a lo ancho: IDENTIDAD (foto, nombre,
   rol, cargo, empresa, actividad) a la izquierda y CREDENCIALES (especializaciones
   y certificaciones) a la derecha. Con `flex-wrap` se apilan solas en pantallas
   estrechas, y la región de credenciales solo se pinta si hay datos (fichas
   escasas quedan limpias).
   --------------------------------------------------------------------------- */

// Card base en fila, sin el hover de "clicable". Borde dorado si es el "asesor del mes".
export const ProfessionalCardRow = styled(CardRow)`
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 20px 30px;
  min-height: 0;
  padding: 22px 24px;
  cursor: default;

  &:hover {
    transform: none;
  }

  &:hover:before {
    opacity: 0;
  }

  ${maxMediaQueries.phone`
    padding: 18px;
  `};

  ${({ $highlighted, theme }) =>
    $highlighted &&
    css`
      border: 2px solid ${theme.colors.accent3};
    `}
`;

// Región izquierda: la persona.
export const Identity = styled.div`
  display: flex;
  flex: 1 1 300px;
  gap: 14px;
  align-items: flex-start;
  min-width: 0;
`;

export const IdentityText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  h3 {
    /* stylelint-disable */
    margin: 0 !important;
    padding: 0 !important;
    /* stylelint-enable */
  }

  /* Badge de rol (AdviserBadge del paquete), con la misma forma que en la ficha de perfil. */
  > span {
    align-self: center;
    padding: 4px 12px;
    border-radius: 999px;
    font-weight: 600;
    line-height: 1;
  }
`;

// Cargo / titular del asesor: máximo 2 líneas para no dominar la card.
export const Tagline = styled.p`
  margin: 5px 0 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.fontL};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Company = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 12px;
`;

// Nombre de la empresa (sustituye al logo). Color neutro a propósito.
export const CompanyText = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.font};
  word-break: break-word;
`;

export const ActivityLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 12px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.fontL};

  b {
    color: ${({ theme }) => theme.colors.font};
    font-weight: 700;
  }
`;

// Región derecha: credenciales. Crece más que la identidad y se apila debajo en estrecho.
export const Meta = styled.div`
  display: flex;
  flex: 2 1 360px;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.fontLL};
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

// Badge dorado "Asesor del mes" (mismo lenguaje visual que el destacado de la Home).
export const MonthBadge = styled.span`
  display: inline-block;
  margin-bottom: 6px;
  padding: 2px 10px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors.accent3};
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
`;

// Certificados como chips "outline" (mismo aspecto que en la ficha de perfil de /usuario).
// Neutros: borde gris y texto tenue, sin dorado.
export const CertBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  > span[type] {
    height: auto;
    min-height: 28px;
    align-items: center;
    margin: 0;
    padding: 3px 10px 3px 4px;
    border-radius: 16px;
    background-color: ${({ theme }) => theme.colors.terciaryLL};
    border: 1px solid ${({ theme }) => theme.colors.ui};
    color: ${({ theme }) => theme.colors.fontL};
    box-shadow: none;
  }

  > span[type] > div {
    display: flex;
    flex: 0 0 auto;
    align-self: center;
    width: 22px;
    height: 22px;
    margin-right: 8px;
    border-radius: 50%;
    overflow: hidden;
  }

  > span[type] > div img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  > span[type] > svg {
    box-sizing: border-box;
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    margin: 0 8px 0 0;
    padding: 4px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.uiLL};
    overflow: visible;
  }

  > span[type] > span {
    padding: 0;
  }
`;
